// react
import React from 'react';

// mui
import { Box, Button, Stack, Tooltip } from '@mui/material';

// components
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { CareCircleFilter } from '@/pwa/organisms/CareCircleFilter/CareCircleFilter';
import { OrganiserEntryItem } from '@/pwa/organisms/OrganiserEntryRow/OrganiserEntryRow';
import { OrganiserEntryGroupList } from '@/pwa/organisms/OrganiserEntryGroupList/OrganiserEntryGroupList';

// types
import { OrganiseEntryStatus } from '@karehero/models';

export interface OrganiserCareCircle {
  id: string;
  name: string;
  color: string;
}

export interface OrganiserPageProps {
  careCircles: OrganiserCareCircle[];
  activeCareCircleIds: string[];
  onActiveCareCircleIdsChange: (next: string[]) => void;
  entries: OrganiserEntryItem[];
  onStatusChange: (entryId: string, status: OrganiseEntryStatus) => void;
  onEntryClick: (entryId: string) => void;
  onAddEntry: () => void;
  onEntryLinkClick?: (link: string) => void;
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  onShowMore?: (currentlyVisible: number, totalUpcoming: number) => void;
  dialogSlot?: React.ReactNode;
}

/**
 * OrganiserPage is the presentational page for the Organiser (todo/calendar) feature.
 * Shows entries from multiple care circles with list and calendar views.
 */
export const OrganiserPage = ({
  careCircles,
  activeCareCircleIds,
  onActiveCareCircleIdsChange,
  entries,
  onStatusChange,
  onEntryClick,
  onAddEntry,
  onEntryLinkClick,
  showCompleted = false,
  onToggleShowCompleted,
  onShowMore,
  dialogSlot,
}: OrganiserPageProps) => {
  return (
    <>
      <PageTemplate
        headerSlot={
          <PageHeader
            title='Actions'
            description='View and manage actions across your care circles.'
          />
        }
        contentSlot={
          <Stack spacing={2}>
            {/* Add task + hide completed */}
            <Stack
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Tooltip
                title={
                  careCircles.length === 0
                    ? 'Create a care circle first to add actions'
                    : ''
                }
              >
                <Box component='span' sx={{ display: 'block', width: '100%' }}>
                  <Button
                    variant='contained'
                    size='large'
                    fullWidth
                    onClick={onAddEntry}
                    disabled={careCircles.length === 0}
                  >
                    Add action
                  </Button>
                </Box>
              </Tooltip>
              {onToggleShowCompleted && (
                <Stack
                  direction='row'
                  sx={{
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                >
                  <Button
                    variant='text'
                    size='small'
                    color='primary'
                    onClick={onToggleShowCompleted}
                  >
                    {showCompleted ? 'Hide completed' : 'Show completed'}
                  </Button>
                </Stack>
              )}
            </Stack>

            {careCircles.length > 1 && (
              <CareCircleFilter
                careCircles={careCircles}
                activeCareCircleIds={activeCareCircleIds}
                onActiveCareCircleIdsChange={onActiveCareCircleIdsChange}
              />
            )}

            <OrganiserEntryGroupList
              entries={entries}
              onStatusChange={onStatusChange}
              onEntryClick={onEntryClick}
              onEntryLinkClick={onEntryLinkClick}
              showCompleted={showCompleted}
              onShowMore={onShowMore}
            />
          </Stack>
        }
      />
      {dialogSlot}
    </>
  );
};

OrganiserPage.displayName = 'OrganiserPage';
export default OrganiserPage;
